<template>
  <PageLayout>
    <template #page-hero>
      <div id="overview">
        <CarDetailHero
          v-if="objectHasEntries(carData)"
          @update:variant-update="(vehicleId) => initCarDetailsPage(vehicleId)"
          :car-data="carData"
          :vehicle-images="vehicleImages"
          :api-status="apiStatus"
        />

        <CarDetailNoResult v-if="apiStatus === 'IDLE' && !objectHasEntries(carData)" :car-details-no-result-props="carDetailsProps" />
      </div>
    </template>

    <StickyScrollSpyNav :nav-items="navItems" :car-data="carData" v-if="carData && Object.keys(carData).length">
      <template #default>
        <div v-for="navData in matchingNavElements" :key="navData.id" :id="navData.id" :data-scrollspy-id="navData.id" class="qmove-container">
          <component :is="navData.component" v-bind="navData.data" :key="variantKey"></component>
        </div>
      </template>
    </StickyScrollSpyNav>
  </PageLayout>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed } from 'vue';
import { useRoute } from 'vue-router';
import { carAdvisoryService } from '../foundation/apiCarAdvisoryService';
import { useCarComparisonStore } from '../foundation/stores/carComparisonStore';
import { ApiStatusTypes } from '../foundation/types/apiStatusTypes';
import { IVehicleImage } from '../foundation/interfaces/IVehicleImage';
import PageLayout from '../layouts/PageLayout.vue';
import CarDetailHero from '../components/car-detail/CarDetailHero.vue';
import CarDetailNoResult from '../components/car-detail/CarDetailNoResult.vue';
import StickyScrollSpyNav from '../components/global/StickyScrollSpyNav.vue';
import CarDetailExpertReview from '../components/car-detail/CarDetailExpertReview.vue';
import { IExpertReview } from '../foundation/interfaces/IExpertReview';
import CarPriceGuide from '../components/global/CarPriceGuide.vue';
import CarDetailCostToRun from '../components/car-detail/CarDetailCostToRun.vue';
import CarSpecsTabularAccordions from '../components/car-stats/CarSpecsTabularAccordions.vue';
import { storeToRefs } from 'pinia';
import { objectHasEntries } from '../foundation/helpers';
import { useCarReviewStore } from '../foundation/stores/carReviewStore';
import { useContentStore } from '../foundation/stores/contentStore';
import { nanoid } from 'nanoid';
// Get the datasourceId from the dynamic component props and pass to GraphQL as a query variable
const props = defineProps<{
  datasourceId: string;
}>();

// Composables
const route = useRoute();
const comparisonStore = useCarComparisonStore();
const carReviewStore = useCarReviewStore();
const contentStore = useContentStore();

contentStore.fetchCarDetailsProps(props.datasourceId);
contentStore.fetchCarDetailsTabularDataProps(props.datasourceId);
contentStore.fetchRegistrationCosts();

const { carDetailsProps, carDetailsTabularDataProps, registrationCosts } = storeToRefs(contentStore);

const variantKey = ref<string>(nanoid());
const apiStatus = ref<ApiStatusTypes>('LOADING');
const vehicleImages = ref<IVehicleImage[]>([]);
const carData = ref<Record<string, any> | undefined>(undefined);
const expertReviewData = ref<Partial<IExpertReview>>({});
const { showCarComparisonFooter } = storeToRefs(comparisonStore);
showCarComparisonFooter.value = false;

// Initialize car details page
onBeforeMount(() => initCarDetailsPage(''));

/**
 * Nav items are hard coded but this may change in the future
 */
const navItems = computed(() => {
  const items = [
    {
      id: 'overview',
      label: 'Overview',
      component: undefined,
      data: undefined,
    },
    {
      id: 'price-guide',
      label: 'Price Guide',
      component: undefined,
      data: undefined,
    },
    {
      id: 'running-costs',
      label: 'Running costs',
      component: undefined,
      data: undefined,
    },
    {
      id: 'specifications',
      label: 'Specifications',
      component: undefined,
      data: undefined,
    },
    {
      id: 'features',
      label: 'Features',
      component: undefined,
      data: undefined,
    },
    {
      id: 'advice',
      label: 'Advice',
      component: undefined,
      data: undefined,
      showInComponent: false,
    },
    {
      id: 'insurance-and-roadside',
      label: 'Insurance and roadside',
      component: undefined,
      data: undefined,
      showInComponent: false,
    },
  ];

  if (objectHasEntries(expertReviewData.value)) {
    items.splice(1, 0, {
      id: 'expert-review',
      label: 'Review',
      component: undefined,
      data: undefined,
    });
  }

  return items.map((item) => ({
    ...item,
    ...getComponentToRender(item.id),
  }));
});

// Only show specific components on the page based on settings in the navItems data.
// In this case, don't show an "overview" component but show the "Overview"
// menu item so the user can scroll to the top of the page.
const matchingNavElements = computed(() => {
  return navItems.value.filter((item: any) => item.showInComponent !== false);
});

/**
 * Reload the car details data whenever variant selection is updated.
 */
function reloadDetailsDueToVariantUpdate() {
  variantKey.value = nanoid();
}

/**
 * Fetch car details data including expert reviews.
 */
async function initCarDetailsPage(vehicleId: string) {
  reloadDetailsDueToVariantUpdate();

  const carId = vehicleId == '' ? (route.params.carId as string) : vehicleId;
  apiStatus.value = 'LOADING';
  if (carId && comparisonStore.fetchedVehicles[carId]) {
    carData.value = comparisonStore.fetchedVehicles[carId];
  }

  try {
    if (!carData.value || carData.value.Vehicle_UID != carId) {
      const data: Record<string, any> = await carAdvisoryService.fetchVehicleById(carId);
      comparisonStore.updateFetchedVehicles(carId, data);
      carData.value = data;
    }

    await carReviewStore.searchReviewByMakeAndModel(carData.value.Make, carData.value.Model);
    const reviews = carReviewStore.getRelevantReview(carData.value.Make, carData.value.Model, carData.value.Trim);

    if (reviews?.document?.review_id) {
      // Fetch expert review data here so that we can update the sticky nav if  review exists
      await carReviewStore.fetchReviewById(reviews.document.review_id);
      expertReviewData.value = carReviewStore.getReviewById(reviews.document.review_id);
    }

    // Fetch vehicle images for the hero carousel
    const vehicleImaData = await carAdvisoryService.fetchVehicleImagesById(carId);
    vehicleImages.value = vehicleImaData.images;
    apiStatus.value = 'IDLE';
  } catch (err) {
    console.error('vehicleDataRequestsError', err);
    apiStatus.value = 'ERROR';
  }
}

/**
 * Get the corresponding Vue component that matches
 * the id of the scroll spy nav id.
 *
 * This allows us to build a unique list of nav items
 * with their own matching custom Vue components.
 *
 * @param id
 */
function getComponentToRender(id: string) {
  switch (id) {
    case 'overview': {
      return {
        showInComponent: false,
      };
    }

    case 'expert-review': {
      return {
        component: CarDetailExpertReview,
        data: {
          expertReviewData: expertReviewData.value,
          apiStatus: apiStatus.value,
          carData: carData?.value,
        },
      };
    }

    case 'price-guide': {
      return {
        component: CarPriceGuide,
        data: {
          isCurrent: carData.value?.Status?.toLowerCase() === 'current',
          carPrice: carData.value?.RACQ_PRICE,
        },
      };
    }

    case 'running-costs': {
      return {
        component: CarDetailCostToRun,
        data: {
          carData: carData?.value,
          carDetailsProps: carDetailsProps.value,
          registrationCosts: registrationCosts.value,
        },
      };
    }

    case 'specifications': {
      return {
        component: CarSpecsTabularAccordions,
        data: {
          tabularData: carDetailsTabularDataProps.value.tabularData,
          carsToCompare: [carData.value],
          applyFilters: true,
          sectionToShow: 'specifications',
        },
      };
    }

    case 'features': {
      return {
        component: CarSpecsTabularAccordions,
        data: {
          tabularData: carDetailsTabularDataProps.value.tabularData,
          carsToCompare: [carData.value],
          applyFilters: true,
          sectionToShow: 'features',
        },
      };
    }

    default: {
      return undefined;
    }
  }
}
</script>

<style lang="scss" scoped>
.car-detail {
  &__page-title {
    padding-top: 2rem;
    margin-bottom: -1rem;
  }

  &__year {
    font-size: 1rem;
    color: $racq-navy;
    font-weight: bold;
    margin: 0;
  }

  &__title {
    font-weight: bold;
    font-size: 2rem;
    margin: 0;
  }

  &__subtitle {
    font-size: 1.75rem;
    margin: 0;
  }
}
</style>
